import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import { AppSettings } from "../../config/app-settings.js";
import Preloader from "../../components/custom/preLoader.js";
import axiosInstance, {
  CheckUserExpiry,
  clearAuthData,
  isCompanyManagedBillingUser,
} from "../../components/content/axiosConfig";
import showErrorToast from "../../components/toast/toast";

const MAX_VISIBLE_FEATURES = 5;

function parseFeatures(features) {
  if (!features) return [];
  if (Array.isArray(features)) return features;
  try {
    const parsed = JSON.parse(features);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function computeCheckoutTotal(pkg, pondCount) {
  const included = pkg.IncludedPonds ?? 1;
  const extra = parseFloat(pkg.ExtraPondAmount) || 0;
  const count = pondCount ?? included;
  const base = parseFloat(pkg.Amount) || 0;
  return Math.round((base + Math.max(0, count - included) * extra) * 100) / 100;
}

function renderPackageDescription(description) {
  if (!description) return null;
  const hasHtml = /<[a-z][\s\S]*>/i.test(description);
  if (hasHtml) {
    return (
      <div
        className="donezo-package-card__desc package-description"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    );
  }
  return <p className="donezo-package-card__desc">{description}</p>;
}

function formatDurationLabel(duration) {
  if (duration === 12) return "/ year";
  return `/ ${duration} month${duration === 1 ? "" : "s"}`;
}

function isPackageActive(pkg) {
  return pkg.Status === true || pkg.Status === 1 || pkg.Status === "1";
}

function normalizePackageList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

function formatEndDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function isActiveSubscriptionInfo(sub) {
  if (!sub) return false;
  return !!(sub.hasSoftwareAccess ?? sub.isSubscriptionActive ?? sub.isActive);
}

const ChoosePanel = () => {
  const context = useContext(AppSettings);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [pondCounts, setPondCounts] = useState({});
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const fetchRequestId = useRef(0);

  useEffect(() => {
    if (isCompanyManagedBillingUser()) {
      showErrorToast("Billing for this account is managed by your Pond Management Company.");
      history.replace("/dashboard");
    }
  }, [history]);

  const CheckExpiry = useCallback(async () => {
    await CheckUserExpiry();
  }, []);

  const handleSignOut = () => {
    clearAuthData();
    window.location.href = "/user/login";
  };

  const fetchChartData = useCallback(async () => {
    const requestId = ++fetchRequestId.current;
    setLoading(true);
    try {
      const packagesRes = await axiosInstance.get("api/Packages/GetAll");
      if (requestId !== fetchRequestId.current) return;
      const pkgList = normalizePackageList(packagesRes.data);
      const activePackages = pkgList.filter(isPackageActive);
      setPackages(activePackages);

      let subData = null;
      try {
        const subRes = await axiosInstance.get("api/fisher/SubscriptionInfo");
        subData = subRes.data;
        setSubscriptionInfo(subData);
      } catch {
        setSubscriptionInfo(null);
      }

      const defaults = {};
      activePackages.forEach((pkg) => {
        const prior =
          subData?.LicensedPondCount && subData?.PackageID === pkg.id
            ? subData.LicensedPondCount
            : pkg.IncludedPonds ?? 1;
        defaults[pkg.id] = prior;
      });
      setPondCounts(defaults);
    } catch (error) {
      if (requestId !== fetchRequestId.current) return;
      const message =
        error.response?.data?.message ||
        (error.message === "Network Error"
          ? "Cannot reach the API server. Check that the backend is running and REACT_APP_Environment in .env points to the correct API."
          : "Unable to load packages. Please try again.");
      showErrorToast(message);
    } finally {
      if (requestId === fetchRequestId.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    context.handleSetAppHeaderNone(false);
    context.handleSetAppContentClass("pondstat-packages-content");

    const syncSidebar = () => {
      const expired = localStorage.getItem("isExpired") === "true";
      context.handleSetAppSidebarNone(expired);
    };

    CheckExpiry().then(syncSidebar);
    fetchChartData();

    return () => {
      context.handleSetAppHeaderNone(false);
      context.handleSetAppContentClass("");
      if (localStorage.getItem("isExpired") !== "true") {
        context.handleSetAppSidebarNone(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!subscriptionInfo) return;
    const active = isActiveSubscriptionInfo(subscriptionInfo);
    context.handleSetAppSidebarNone(!active);
  }, [subscriptionInfo, context]);

  const setPondCount = (pkgId, value) => {
    const count = Math.max(1, parseInt(value, 10) || 1);
    setPondCounts((prev) => ({ ...prev, [pkgId]: count }));
  };

  const getPaymentHref = (pkg) => {
    const count = pondCounts[pkg.id] ?? pkg.IncludedPonds ?? 1;
    if (pkg.AllowsExtraPonds) {
      return `/payment/${pkg.id}?ponds=${count}`;
    }
    return `/payment/${pkg.id}?ponds=1`;
  };

  const subscriptionActive = isActiveSubscriptionInfo(subscriptionInfo);
  const currentPackageId = subscriptionInfo?.PackageID ?? subscriptionInfo?.package?.id ?? null;
  const currentPlanName =
    subscriptionInfo?.packageName ||
    subscriptionInfo?.package?.Name ||
    (subscriptionInfo?.isTrialPeriod ? "Free trial" : null);
  const currentEndDate = formatEndDate(subscriptionInfo?.EndDate);
  const licensedPonds = subscriptionInfo?.LicensedPondCount ?? 1;

  if (loading) return <Preloader />;

  return (
    <div className="donezo-page donezo-list-page donezo-orders-page donezo-upload-page donezo-packages-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4 donezo-packages-page__inner">
        <div className="donezo-orders-toolbar donezo-upload-toolbar">
          <div className="donezo-upload-toolbar__intro">
            <h1 className="donezo-orders-toolbar__title">
              {subscriptionActive ? "Change or renew your plan" : "Choose your package"}
            </h1>
            <p className="donezo-upload-toolbar__subtitle">
              {subscriptionActive
                ? "Compare plans and switch anytime. Licensed pond count stays editable on multi-pond packages."
                : subscriptionInfo?.LicensedPondCount > 1
                  ? `Previous plan: ${subscriptionInfo.LicensedPondCount} licensed ponds`
                  : "Select a plan to continue using PondStat."}
            </p>
          </div>
          <div className="donezo-orders-toolbar__actions">
            {subscriptionActive ? (
              <Link to="/dashboard" className="donezo-orders-toolbar__primary">
                Dashboard
              </Link>
            ) : (
              <button
                type="button"
                className="donezo-orders-toolbar__ghost donezo-packages-page__signout"
                onClick={handleSignOut}
              >
                <i className="fa fa-sign-out" aria-hidden />
                <span>Sign out</span>
              </button>
            )}
          </div>
        </div>

        {subscriptionActive && currentPlanName && (
          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header donezo-upload-section__header--split">
              <div className="d-flex align-items-center gap-2">
                <i className="fa fa-check-circle" aria-hidden />
                <h2 className="donezo-upload-section__title">Current subscription</h2>
              </div>
              <span className="donezo-status-dot donezo-status-dot--natural">
                <span className="donezo-status-dot__mark" aria-hidden />
                Active
              </span>
            </div>
            <div className="donezo-upload-section__body">
              <div className="donezo-packages-current">
                <div>
                  <p className="donezo-packages-current__name">{currentPlanName}</p>
                  <p className="donezo-packages-current__meta mb-0">
                    {licensedPonds} licensed pond{licensedPonds === 1 ? "" : "s"}
                    {currentEndDate ? ` · until ${currentEndDate}` : ""}
                  </p>
                  {subscriptionInfo?.isTrialPeriod && (
                    <p className="donezo-upload-section__hint mb-0 mt-2">
                      Free trial — choose a paid plan before it ends.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {!subscriptionActive &&
          subscriptionInfo?.EndDate &&
          !subscriptionInfo?.hasSoftwareAccess && (
            <div className="donezo-upload-notice donezo-upload-notice--warn mb-3">
              Subscription expired {formatEndDate(subscriptionInfo.EndDate)}. Pick a plan to restore
              access.
            </div>
          )}

        {subscriptionActive &&
          subscriptionInfo?.PackageID &&
          subscriptionInfo?.isSubscriptionActive === false && (
            <div className="donezo-upload-notice mb-3">
              Your billing period ended {formatEndDate(subscriptionInfo.EndDate)}. You still have
              access with your current package. Renew anytime to extend your subscription.
            </div>
          )}

        {packages.length === 0 ? (
          <div className="donezo-orders-panel">
            <div className="donezo-empty-state donezo-empty-state--compact">
              <div className="donezo-empty-state__icon" aria-hidden>
                <i className="fa fa-box" />
              </div>
              <p className="donezo-empty-state__title">No packages available</p>
              <p className="donezo-empty-state__text">
                Contact your administrator if you need a plan assigned.
              </p>
            </div>
          </div>
        ) : (
          <div className="row g-3 donezo-packages-grid">
            {packages.map((pkg) => {
              const featureList = parseFeatures(pkg.Features);
              const visibleFeatures = featureList.slice(0, MAX_VISIBLE_FEATURES);
              const hiddenFeatureCount = featureList.length - visibleFeatures.length;
              const pondCount = pondCounts[pkg.id] ?? pkg.IncludedPonds ?? 1;
              const total = computeCheckoutTotal(pkg, pondCount);
              const extraPerPond = parseFloat(pkg.ExtraPondAmount) || 0;
              const isCurrentPackage =
                subscriptionActive &&
                currentPackageId != null &&
                Number(currentPackageId) === Number(pkg.id);

              return (
                <div key={pkg.id} className="col-lg-6">
                  <div
                    className={`donezo-orders-panel donezo-package-card${
                      isCurrentPackage ? " is-current" : ""
                    }`}
                  >
                    <div className="donezo-upload-section__header donezo-upload-section__header--split">
                      <div className="d-flex align-items-center gap-2">
                        <i className="fa fa-box" aria-hidden />
                        <h2 className="donezo-upload-section__title">{pkg.Name}</h2>
                      </div>
                      {isCurrentPackage ? (
                        <span className="donezo-status-dot donezo-status-dot--natural">
                          <span className="donezo-status-dot__mark" aria-hidden />
                          Current plan
                        </span>
                      ) : (
                        <span className="donezo-package-card__icon" aria-hidden>
                          <i className="fa fa-leaf" />
                        </span>
                      )}
                    </div>

                    <div className="donezo-upload-section__body donezo-package-card__body">
                      <div className="donezo-package-card__price">
                        ${Number(pkg.Amount).toFixed(2)}{" "}
                        <small>{formatDurationLabel(pkg.Duration)}</small>
                      </div>
                      {pkg.AllowsExtraPonds && extraPerPond > 0 && (
                        <div className="donezo-package-card__extra">
                          +${extraPerPond.toFixed(2)}/extra pond
                        </div>
                      )}

                      {renderPackageDescription(pkg.Description)}

                      {visibleFeatures.length > 0 && (
                        <ul className="donezo-package-card__features">
                          {visibleFeatures.map((feature) => (
                            <li key={feature}>
                              <i className="fa fa-check" aria-hidden />
                              <span>{feature}</span>
                            </li>
                          ))}
                          {hiddenFeatureCount > 0 && (
                            <li className="donezo-package-card__more">
                              +{hiddenFeatureCount} more feature
                              {hiddenFeatureCount === 1 ? "" : "s"}
                            </li>
                          )}
                        </ul>
                      )}

                      {pkg.AllowsExtraPonds && !isCurrentPackage && (
                        <div className="donezo-package-card__pond-picker">
                          <label className="form-label small fw-medium mb-1">
                            Ponds to license
                          </label>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              className="donezo-package-card__stepper"
                              onClick={() => setPondCount(pkg.id, pondCount - 1)}
                              aria-label="Decrease pond count"
                            >
                              −
                            </button>
                            <input
                              type="number"
                              min="1"
                              className="form-control form-control-sm text-center donezo-package-card__count"
                              value={pondCount}
                              onChange={(e) => setPondCount(pkg.id, e.target.value)}
                            />
                            <button
                              type="button"
                              className="donezo-package-card__stepper"
                              onClick={() => setPondCount(pkg.id, pondCount + 1)}
                              aria-label="Increase pond count"
                            >
                              +
                            </button>
                            <span className="donezo-package-card__total">
                              Total <strong>${total.toFixed(2)}</strong>
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="donezo-package-card__cta">
                        {isCurrentPackage ? (
                          <button
                            type="button"
                            className="donezo-orders-toolbar__ghost w-100 justify-content-center"
                            disabled
                          >
                            Current plan
                          </button>
                        ) : (
                          <Link
                            to={getPaymentHref(pkg)}
                            className="donezo-orders-toolbar__primary w-100 justify-content-center"
                          >
                            {subscriptionActive ? "Switch plan" : "Get started"}
                            <i className="fa fa-arrow-right ms-1" aria-hidden />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="donezo-packages-page__footer">© PondStat. All Rights Reserved</p>
      </div>
    </div>
  );
};

export default ChoosePanel;
